/**
 * @fileoverview Whether the model is this gardener's to use.
 *
 * The two features that cost money per use — the tray's "what is this?" and
 * the plant doctor — are for subscribers. Asking before offering is the
 * difference between a button that says what it costs and one that looks
 * available and then fails.
 *
 * The answer comes from the server, which decides it with the same rule that
 * guards the endpoints, allowlist and all. Unknown reads as available: a
 * billing call that has not landed should not make the app look locked, and
 * the gate itself is the thing that actually refuses.
 *
 * The owner can switch a gardener's AI off on the Oversight page whatever
 * their plan (revoked): the screens then say that, calmly, never Subscribe.
 *
 * Where: the photo tray, the seed-packet scan and the plant check-up, to
 * decide whether to offer their AI buttons.
 * API: GET /billing/status (through useBilling), access.ai and
 * access.aiRevoked.
 * Settings: the owner's Oversight switch (aiRevoked) and the server's AI
 * allow list.
 *
 * @module useAiAccess
 */

import { useBilling } from './useBilling';

/** Whether the model may be offered, and whether that is known yet. */
export interface AiAccess {
  /** False only when the server has said so. */
  allowed: boolean;
  /** True while we do not know yet. */
  loading: boolean;
  /**
   * The owner switched AI off for this account (or the garden's owner): the
   * screens say so (billing.aiRevoked) instead of offering Subscribe.
   */
  revoked: boolean;
}

/**
 * Whether to offer the model's features; available until the server says otherwise.
 *
 * @returns Whether it is allowed, whether that is known yet, and whether the owner switched it off.
 */
export function useAiAccess(): AiAccess {
  const { data, isLoading } = useBilling();
  const access = data?.access;
  const revoked = access?.aiRevoked === true;
  return {
    allowed: !revoked && access?.ai !== false,
    loading: isLoading || !data,
    revoked,
  };
}
